import { RefObject, useEffect } from 'react';
import { getArrowFocusables, resolveActiveFocusable } from './focusable';

const ARROW_NEXT = new Set(['ArrowDown', 'ArrowRight']);
const ARROW_PREV = new Set(['ArrowUp', 'ArrowLeft']);

/** Move focus between items with arrow keys (e.g. settings dialog lists). */
export function useArrowFocusList(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean
) {
  useEffect(() => {
    if (!active) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (!ARROW_NEXT.has(event.key) && !ARROW_PREV.has(event.key)) return;

      const container = containerRef.current;
      if (!container || !container.contains(document.activeElement)) return;

      const activeElement = document.activeElement as HTMLElement;
      const focusables = getArrowFocusables(container, activeElement);
      if (focusables.length < 2) return;

      // Title/initial focus may be tabIndex=-1 and absent from the list — still
      // enter the list. Prefer the nearest focusable in arrow direction (so
      // Documents title → first policy link, not the Close button above it).
      const activeEl = resolveActiveFocusable(container, focusables);

      event.preventDefault();
      event.stopPropagation();

      let index = activeEl ? focusables.indexOf(activeEl) : -1;
      const next = ARROW_NEXT.has(event.key);

      if (index === -1) {
        const focused = document.activeElement as HTMLElement;
        let nextEl: HTMLElement;

        if (next) {
          nextEl =
            focusables.find(
              el =>
                !!(
                  focused.compareDocumentPosition(el) &
                  Node.DOCUMENT_POSITION_FOLLOWING
                )
            ) ?? focusables[0];
        } else {
          const preceding = focusables.filter(
            el =>
              !!(
                focused.compareDocumentPosition(el) &
                Node.DOCUMENT_POSITION_PRECEDING
              )
          );
          nextEl =
            preceding[preceding.length - 1] ??
            focusables[focusables.length - 1];
        }

        nextEl.focus();
        nextEl.scrollIntoView({ block: 'nearest' });
        return;
      }

      if (next) {
        index = index >= focusables.length - 1 ? 0 : index + 1;
      } else {
        index = index <= 0 ? focusables.length - 1 : index - 1;
      }

      const nextEl = focusables[index];
      nextEl.focus();
      nextEl.scrollIntoView({ block: 'nearest' });
    };

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [active, containerRef]);
}
